import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `/api`;

const SLPLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/slp/login`, { email, password }, { withCredentials: true });
      toast.success(`Welcome back, ${response.data.slp_name}!`);
      onLogin(response.data);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#2C4C3B] rounded-2xl mb-4">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-4xl font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>FrameEd</h1>
          <p className="text-[#595959]">Referrals</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8">
          <h2 className="text-xl font-semibold text-[#1C1C1C] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>SLP Login</h2>
          <p className="text-sm text-[#595959] mb-6">Manage your school's referral system</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">
                <Mail className="w-4 h-4 inline mr-2 text-[#595959]" />
                Email Address
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@school.edu"
                className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">
                <Lock className="w-4 h-4 inline mr-2 text-[#595959]" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full h-11 px-4 pr-11 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#595959] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium rounded-xl px-6 py-3 transition-colors disabled:bg-[#E5E5E5] disabled:text-[#595959] disabled:cursor-default"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-[#595959] hover:text-[#2C4C3B] transition-colors">Forgot password?</Link>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E5E5E5] text-center space-y-2">
            <p className="text-sm text-[#595959]">
              Don't have an account?{' '}
              <Link to="/slp/signup" className="text-[#2C4C3B] font-medium hover:underline">Sign up free</Link>
            </p>
            <p className="text-sm text-[#595959]">
              Are you a teacher?{' '}
              <Link to="/login" className="text-[#2C4C3B] font-medium hover:underline">Teacher Login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SLPLogin;
